import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getSession, getFluencyPracticeMetrics, saveFluencyBestStreak } from '../../config/api';
import { Fact } from '../../types';
import SelectionScreen from './SelectionScreen';
import QuestionScreen from './QuestionScreen';
import { motion } from 'framer-motion';
import { useSession } from '../../context/SessionContext';
import { playCorrectSound, playStreakSound } from '../FluencyDrill/sounds';
import { updatePracticeContext } from '../../utils/practiceContext';

type Screen = 'selection' | 'question';

export interface AccuracyPracticeSettings {
  facts: Fact[];
}

// Define a type for facts with progressStatus
interface FactWithStatus extends Fact {
  progressStatus?: string;
}

const AccuracyPracticePage: React.FC = () => {
  const { user } = useAuth();
  const { recordPageTransition } = useSession();
  const navigate = useNavigate();
  const [screen, setScreen] = useState<Screen>('selection');
  const [facts, setFacts] = useState<Fact[]>([]);
  const [practiceFacts, setPracticeFacts] = useState<FactWithStatus[]>([]);
  const [otherFacts, setOtherFacts] = useState<FactWithStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(3);

  // Best streak from backend
  const [bestStreak, setBestStreak] = useState<number | null>(null);

  // Streak tracking refs (persist across re-renders)
  const currentStreakRef = useRef<number>(0);
  const sessionBestStreakRef = useRef<number>(0);

  // Streak state (mirrors refs, drives UI re-renders)
  const [currentStreak, setCurrentStreak] = useState(0);
  const [sessionBestStreak, setSessionBestStreak] = useState(0);

  const handleAnswerResult = useCallback((correct: boolean) => {
    if (correct) {
      playCorrectSound();
      currentStreakRef.current += 1;
      setCurrentStreak(currentStreakRef.current);
      if (currentStreakRef.current > sessionBestStreakRef.current) {
        sessionBestStreakRef.current = currentStreakRef.current;
        setSessionBestStreak(sessionBestStreakRef.current);
      }
      if (currentStreakRef.current >= 3 && currentStreakRef.current % 2 === 1) {
        playStreakSound(currentStreakRef.current);
      }
    } else {
      currentStreakRef.current = 0;
      setCurrentStreak(0);
    }
  }, []);

  useEffect(() => {
    // Update practice context when page loads
    const practiceContext = updatePracticeContext('accuracy');

    const fetchSessionData = async () => {
      if (!user?.userId) return;
      
      try {
        setIsLoading(true);
        const [sessionResponse, metricsResponse] = await Promise.all([
          getSession(user.userId, 'accuracyPractice'),
          getFluencyPracticeMetrics(user.userId).catch(() => ({ bestStreak: null }))
        ]);
        setBestStreak(metricsResponse.bestStreak);
        
        if (!sessionResponse.availableActivities.accuracyPractice) {
          setError('No accuracy practice activities available for today.');
          setFacts([]);
          setPracticeFacts([]);
          setOtherFacts([]);
          return;
        }
        
        const accuracyFacts = sessionResponse.availableActivities.accuracyPractice.facts;
        setFacts(accuracyFacts);
        
        const allFacts = sessionResponse.availableActivities.accuracyPractice.facts as FactWithStatus[];
        
        // Group facts based on progressStatus
        const practice: FactWithStatus[] = [];
        const others: FactWithStatus[] = [];
        
        allFacts.forEach(fact => {
          // Include facts with status 'accuracyPractice' in practice facts
          if (fact.progressStatus === 'accuracyPractice') {
            practice.push(fact);
          } else {
            others.push(fact);
          }
        });
        
        // Store the grouped facts
        setPracticeFacts(practice);
        setOtherFacts(others);
        
        // Set all facts for the UI
        setFacts(allFacts);
        
        // Record page transition with ONLY practiceFacts IDs, not all facts
        if (practice.length > 0) {
          const practiceFactIds = practice.map(fact => fact.factId);
          recordPageTransition('accuracy-practice', {
            accuracyPractice: practiceFactIds
          });
        } else {
          recordPageTransition('accuracy-practice');
        }
        
        setError(null);
      } catch (error) {
        console.error('Failed to fetch session data:', error);
        setError('Failed to load accuracy practice. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSessionData();
  }, [user?.userId]);
  
  // Redirect to fluency practice if no facts are available
  useEffect(() => {
    if (!isLoading && facts.length === 0) {
      setRedirecting(true);

      const countdownInterval = setInterval(() => {
        setRedirectCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            // Redirect to fluency practice
            window.location.href = '/fluency-practice';
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [isLoading, facts.length]);
  
  const handleStart = (settings: AccuracyPracticeSettings) => {
    setFacts(settings.facts);
    setScreen('question');
  };
  
  const handleComplete = () => {
    // Save best streak if > 0 (fire-and-forget)
    if (sessionBestStreakRef.current > 0 && user?.userId) {
      saveFluencyBestStreak(user.userId, sessionBestStreakRef.current).catch(() => {});
    }
    // Carry streak forward to fluency practice for continuous UX
    sessionStorage.setItem('carriedStreak', JSON.stringify({
      currentStreak: currentStreakRef.current,
      sessionBestStreak: sessionBestStreakRef.current,
    }));
    navigate('/fluency-practice');
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Loading accuracy practice...</p>
        </div>
      </div>
    );
  }
  
  if (redirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Skipping to fluency practice... ({redirectCountdown})</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {screen === 'selection' && (
        <SelectionScreen
          onStart={handleStart}
          facts={facts}
          errorMessage={error}
          bestStreak={bestStreak}
          totalFacts={facts.length}
        />
      )}
      
      {screen === 'question' && facts.length > 0 && (
        <QuestionScreen
          questions={facts}
          practiceFacts={practiceFacts.map(fact => fact.PK)}
          otherFacts={otherFacts.map(fact => fact.PK)}
          onComplete={handleComplete}
          onAnswerResult={handleAnswerResult}
          currentStreak={currentStreak}
          sessionBestStreak={Math.max(bestStreak ?? 0, sessionBestStreak)}
        />
      )}
    </div>
  );
};

export default AccuracyPracticePage; 